import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSettings, saveSettings, testEmail } from "@/lib/api";
import { enablePush, disablePush, pushSupported, testPush } from "@/lib/push";
import { playSound, SOUND_PRESETS } from "@/lib/sounds";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Bell, Mail, Sliders, BellRing, Check, Send, Volume2, Play, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const Row = ({ icon: Icon, title, desc, children }) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-border-subtle">
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-secondary mt-0.5" />
      <div>
        <div className="text-sm text-white">{title}</div>
        <div className="text-xs text-tertiary">{desc}</div>
      </div>
    </div>
    <div>{children}</div>
  </div>
);

export const SettingsPanel = () => {
  const { data } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [s, setS] = useState(null);
  useEffect(() => { if (data) setS(data); }, [data, setS]);
  if (!s) return <div className="text-tertiary font-mono text-xs p-8">Loading settings…</div>;

  const update = (patch) => setS((p) => ({ ...p, ...patch }));

  const persist = async (next) => {
    const merged = { ...s, ...next };
    setS(merged);
    await saveSettings(merged);
  };

  const togglePush = async (val) => {
    if (val) {
      if (!pushSupported()) { toast.error("Push not supported in this browser"); return; }
      try {
        await enablePush();
        persist({ push_enabled: true });
        toast.success("Background push enabled — alerts arrive even when the app is closed");
      } catch (e) {
        toast.error(e.message || "Could not enable push");
      }
    } else {
      try { await disablePush(); } catch (e) {}
      persist({ push_enabled: false });
      toast.success("Push notifications disabled");
    }
  };

  const onTestPush = async () => {
    try { const r = await testPush(); toast.success(`Test push sent to ${r.sent_to} device(s)`); }
    catch (e) { toast.error(e.message || "Enable push first"); }
  };

  const onSave = async () => { await saveSettings(s); toast.success("Settings saved"); };
  const onTest = async () => {
    if (!s.email) { toast.error("Enter an email first"); return; }
    try { await testEmail(s.email); toast.success(`Test email sent to ${s.email}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Email not configured. Add RESEND_API_KEY."); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="settings-panel">
      <div className="bg-surface border border-border rounded-lg p-5">
        <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-white mb-2">Notifications</h3>
        <Row icon={BellRing} title="Push notifications" desc="Background browser alerts, even when the app is closed">
          <Switch data-testid="toggle-push" checked={s.push_enabled} onCheckedChange={togglePush} />
        </Row>
        <div className="flex justify-end -mt-2 mb-1">
          <button data-testid="test-push-btn" onClick={onTestPush}
            className="flex items-center gap-1.5 text-[10px] font-mono text-ai hover:text-white uppercase transition-colors">
            <Send size={11} /> Send test push
          </button>
        </div>
        <Row icon={Mail} title="Email alerts" desc="Get signal digests by email (Resend)">
          <Switch data-testid="toggle-email" checked={s.email_enabled} onCheckedChange={(v) => persist({ email_enabled: v })} />
        </Row>
        <Row icon={Volume2} title="Alert sound" desc="Play a chime on breakout / breakdown / rule signals">
          <Switch data-testid="toggle-sound" checked={s.sound_enabled ?? true} onCheckedChange={(v) => persist({ sound_enabled: v })} />
        </Row>
        {(s.sound_enabled ?? true) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-tertiary font-mono flex items-center gap-1 mb-1">
                <TrendingUp size={11} className="text-bull" /> Bullish signal sound
              </label>
              <div className="flex gap-2">
                <Select value={s.sound_bullish || "rise"} onValueChange={(v) => persist({ sound_bullish: v })}>
                  <SelectTrigger data-testid="sound-bullish" className="bg-app border-border text-sm flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-border">
                    {Object.entries(SOUND_PRESETS).map(([id, p]) => <SelectItem key={id} value={id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button data-testid="preview-bullish" onClick={() => playSound(s.sound_bullish || "rise")}
                  className="bg-surface-2 hover:bg-surface-3 border border-border rounded px-2.5 text-bull"><Play size={13} /></button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-tertiary font-mono flex items-center gap-1 mb-1">
                <TrendingDown size={11} className="text-bear" /> Bearish signal sound
              </label>
              <div className="flex gap-2">
                <Select value={s.sound_bearish || "drop"} onValueChange={(v) => persist({ sound_bearish: v })}>
                  <SelectTrigger data-testid="sound-bearish" className="bg-app border-border text-sm flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-border">
                    {Object.entries(SOUND_PRESETS).map(([id, p]) => <SelectItem key={id} value={id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button data-testid="preview-bearish" onClick={() => playSound(s.sound_bearish || "drop")}
                  className="bg-surface-2 hover:bg-surface-3 border border-border rounded px-2.5 text-bear"><Play size={13} /></button>
              </div>
            </div>
          </div>
        )}
        <div className="pt-4 flex flex-col sm:flex-row gap-2">
          <Input data-testid="email-input" placeholder="you@email.com" value={s.email}
            onChange={(e) => update({ email: e.target.value })}
            className="bg-app border-border font-mono text-sm" />
          <button data-testid="test-email-btn" onClick={onTest}
            className="bg-surface-2 hover:bg-surface-3 border border-border text-white font-mono text-xs px-4 py-2 rounded whitespace-nowrap transition-colors">
            Send test email
          </button>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-lg p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sliders size={15} className="text-ai" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">Alert Thresholds</h3>
        </div>
        <Row icon={Bell} title="Min relative volume" desc="Trigger on volume spikes above this multiple">
          <Input data-testid="min-relvol" type="number" step="0.1" value={s.min_rel_volume}
            onChange={(e) => update({ min_rel_volume: parseFloat(e.target.value) || 0 })}
            className="w-20 bg-app border-border font-mono text-sm text-right" />
        </Row>
        <Row icon={Bell} title="RSI oversold" desc="Bullish bounce alerts below this RSI">
          <Input data-testid="rsi-oversold" type="number" value={s.rsi_oversold}
            onChange={(e) => update({ rsi_oversold: parseFloat(e.target.value) || 0 })}
            className="w-20 bg-app border-border font-mono text-sm text-right" />
        </Row>
        <Row icon={Bell} title="RSI overbought" desc="Bearish fade alerts above this RSI">
          <Input data-testid="rsi-overbought" type="number" value={s.rsi_overbought}
            onChange={(e) => update({ rsi_overbought: parseFloat(e.target.value) || 0 })}
            className="w-20 bg-app border-border font-mono text-sm text-right" />
        </Row>
        <Row icon={Bell} title="Track penny stocks" desc="Include sub-$5 tickers in alerts">
          <Switch data-testid="toggle-penny" checked={s.watch_penny} onCheckedChange={(v) => update({ watch_penny: v })} />
        </Row>
        <Row icon={Bell} title="Track ADRs" desc="Include foreign ADR tickers in alerts">
          <Switch data-testid="toggle-adr" checked={s.watch_adr} onCheckedChange={(v) => update({ watch_adr: v })} />
        </Row>
      </div>

      <button data-testid="save-settings-btn" onClick={onSave}
        className="flex items-center gap-2 bg-neutral hover:bg-neutral/80 text-white font-semibold text-sm px-5 py-2.5 rounded transition-colors">
        <Check size={15} /> Save settings
      </button>
    </div>
  );
};
